import { useCallback, useMemo } from 'react';
import { isAndroid, isIOS } from 'react-device-detect';

import { subscriptionMainPageRegex } from '../../../constants/regex';
import { MAX_WIDTH_639PX } from '../../common';
import { FIXED_BOTTOM_HEIGHT } from '../../common/fixed-bottom-height';
import { SAFE_AREA_INSET_BOTTOM } from '../../common/safe-area';
import { ReactNativeGtm } from '../../gtm/react-native/ReactNativeGtm';
import { isReactNative } from '../../react-native';
import useClientMediaQuery from '../useClientMediaQuery';
import useClientSide from '../useClientSide';
import { usePathnameMethod } from '../usePathnameMethod';

/**
 * 바텀네브 노출 페이지
 *
 * @link https://uglyushq.slack.com/archives/C03G2RC70NA/p1683866383425989?thread_ts=1683859845.819049&cid=C03G2RC70NA
 */
export const useBottomNavigation = () => {
  const { pathname } = usePathnameMethod();
  const isClientSide = useClientSide();
  const isMobile = useClientMediaQuery({ query: MAX_WIDTH_639PX });

  /** /subscriptions/{id}/main 패턴 체크 */
  const isSubscriptionMainPage = useMemo(
    () => subscriptionMainPageRegex.test(pathname),
    [pathname],
  );

  /**
   * 현재 path가 BottomNavigation이 노출되는 경로인지 — pathname만으로 판단하여 SSR/CSR 동일한 값을 보장.
   * client-only 측정에 의존하지 않으므로 레이아웃 계산에서 mount 직후 layout shift를 일으키지 않는다.
   */
  const isRouteWithBottomNavigation = useMemo(
    () =>
      [
        '/main', // 정기배송
        '/ustore', // 마켓
        '/recipes', // 레시피
        '/refrigerator', // 마이냉장고
        '/mypages', // 마이페이지
        '/mission', // 미션
        '/blogs', // 블로그
      ].includes(pathname) || isSubscriptionMainPage,
    [pathname, isSubscriptionMainPage],
  );

  /**
   * 실제로 BottomNavigation이 화면에 그려지는지 — desktop과 SSR에서는 false. BottomNavigation 컴포넌트의 렌더링 분기 용도.
   */
  const isShowBottomNavigation = useMemo(
    () => isClientSide && isMobile && isRouteWithBottomNavigation,
    [isClientSide, isMobile, isRouteWithBottomNavigation],
  );

  const addBottomHeightRN = useCallback(
    (height: number): string => {
      const base = isShowBottomNavigation
        ? height + FIXED_BOTTOM_HEIGHT.BOTTOM_NAVIGATION
        : height;
      // SSR→CSR 전환 시 값이 달라져야 React가 DOM을 업데이트하고
      // iOS Chrome이 env(safe-area-inset-bottom)을 재평가한다
      return isClientSide
        ? `calc(${base}px + ${SAFE_AREA_INSET_BOTTOM})`
        : `${base}px`;
    },
    [isShowBottomNavigation, isClientSide],
  );

  return useMemo(
    () => ({
      isShowBottomNavigation,
      isRouteWithBottomNavigation,
      addBottomHeightRN,
    }),
    [isShowBottomNavigation, isRouteWithBottomNavigation, addBottomHeightRN],
  );
};

const playStoreLink =
  'https://play.google.com/store/apps/details?id=com.uglyusapp&hl=ko-KR';
const appStoreLink = 'https://apps.apple.com/kr/app/어글리어스/id6449239684';
const isPCWeb = !(isIOS || isAndroid);

const checkInstallApp = () => {
  const clearTimers = () => {
    clearInterval(check);
    clearTimeout(timer);
  };

  // 앱이 열렸는지 확인 (브라우저가 백그라운드로 가면 document.hidden = true)
  const isHideWeb = () => {
    if (document.hidden) {
      clearTimers();
    }
  };
  const check = setInterval(isHideWeb, 200);

  const timer = setTimeout(redirectStore, 500);
};

const redirectStore = () => {
  if (!window) return console.error('window is undefined!!');

  new ReactNativeGtm().click_app_install();

  window.location.href = isAndroid ? playStoreLink : appStoreLink;
};

const openNativeApp = () => {
  const url = isAndroid ? 'uglyus://app' : 'uglyus://';

  if (!window) return console.error('window is undefined!!');

  window.location.href = url;
};

export const redirectApp = () => {
  //isApp => 배너 미노출
  //isWeb => 배너노출 - 앱다운로드 스토어 주소 연결

  // PC웹 또는 모바일웹에서 동작
  if (isPCWeb || !isReactNative()) {
    openNativeApp();
    checkInstallApp();
  }
};
